import { useLocalePathname, useLocaleRouter } from "@i18n/routing";
import {
	HOTEL_LOBBY_PAGE_PATH,
	HOTEL_LOBBY_PROMPT_PATTERN,
} from "@marketing/hotel-lobby/config/hotel-lobby-preset";
import { normalizePage } from "@saas/payments/lib/track-page";
import {
	EFFECTS_CATALOG,
	type EffectCatalogItem,
} from "@saas/shared/config/effects-catalog";
import { useCallback } from "react";
import type { VideoHistoryItem } from "./types";
import {
	VIDEO_ACTION_STORAGE_KEY,
	type VideoHistoryAction,
} from "./video-action-handoff";

/**
 * 作品回它出生的页面（2026-10-23 老公定）：History / 详情弹窗里点 Reprompt / Regenerate，
 * 回到做出这条视频的那个页面去填、去重跑。特效页的片在 /image-to-video 回填会把隐藏模板整段倒进
 * 表单，重跑还丢原曲（preset-audio 按「提交页面|模型」配）。
 *
 * 跨页带的是整条记录：sessionStorage 的 videoActionData + ?action=，和生图页、My Assets 原来
 * 跳 /image-to-video 是同一条通道，ImageToVideoClient 那边早就会接。
 */

/** 认出生页只看这两列；详情弹窗拿到的行类型比 VideoHistoryItem 宽松 */
type VideoHomeSource = Pick<VideoHistoryItem, "prompt" | "metadata">;

/** 这三个页面是同一个表单（ImageToVideoClient），互相之间不用跳 */
const I2V_WORKBENCH_PAGES = [
	"/image-to-video",
	"/reference-to-video",
	"/start-end-frame-video",
];

/**
 * 也用 ImageToVideoClient，但藏了模式 tab、只给两个模型、必须传视频（/genjutsu-ai）；
 * /ai-music-video-generator 同理，只有 OmniHuman、必须传歌。
 * 算出生页，但不和上面三个互认：别处出的片在这里回填会卡在切不回的模式或被换掉的模型上
 */
const RESTRICTED_WORKBENCH_PAGES = [
	"/genjutsu-ai",
	"/ai-music-video-generator",
];

const VIDEO_EFFECTS = EFFECTS_CATALOG.filter((item) => item.kind === "video");

/**
 * 10/8 起提交时记了 metadata.page。更早的特效片只有 Hotel Lobby（10/1 上线），按模板开头认；
 * 首页、模型落地页这些通用表单出的片，还有认不出的老片，都回 /image-to-video
 */
export function getVideoHomePage(item: VideoHomeSource): string {
	const page = item.metadata?.page;
	if (
		page &&
		(I2V_WORKBENCH_PAGES.includes(page) ||
			RESTRICTED_WORKBENCH_PAGES.includes(page) ||
			VIDEO_EFFECTS.some((effect) => effect.href === page))
	) {
		return page;
	}
	if (HOTEL_LOBBY_PROMPT_PATTERN.test(item.prompt)) {
		return HOTEL_LOBBY_PAGE_PATH;
	}
	return "/image-to-video";
}

/** 特效页出的片：卡头写 EFFECT · 特效名 */
export function getVideoEffect(
	item: VideoHomeSource,
): EffectCatalogItem | undefined {
	const home = getVideoHomePage(item);
	return VIDEO_EFFECTS.find((effect) => effect.href === home);
}

/** 各特效页 *_NOTE_PREFIX 都是这一句 */
const EFFECT_NOTE_PREFIX = "\n\nDirector's note: ";

/**
 * 卡片和详情弹窗上显示的 prompt。特效页的 prompt 是隐藏模板 + 可选备注，只露用户自己写的备注，
 * 没写备注就是空串（2026-10-28 老公定）。
 */
export function getVideoDisplayPrompt(item: VideoHomeSource): string {
	if (!getVideoEffect(item)) {
		return item.prompt;
	}
	const index = item.prompt.indexOf(EFFECT_NOTE_PREFIX);
	return index >= 0
		? item.prompt.slice(index + EFFECT_NOTE_PREFIX.length)
		: "";
}

function isSameHome(home: string, current: string) {
	return (
		home === current ||
		(I2V_WORKBENCH_PAGES.includes(home) &&
			I2V_WORKBENCH_PAGES.includes(current))
	);
}

/** 返回 true 表示已经跳去出生页了；false 表示本页就是出生页，由调用方原地回填 */
export function useVideoHomeRouter() {
	const router = useLocaleRouter();
	const current = normalizePage(useLocalePathname());
	return useCallback(
		(item: VideoHomeSource, action: VideoHistoryAction): boolean => {
			const home = getVideoHomePage(item);
			if (isSameHome(home, current)) {
				return false;
			}
			sessionStorage.setItem(
				VIDEO_ACTION_STORAGE_KEY,
				JSON.stringify(item),
			);
			router.push(`${home}?action=${action}`);
			return true;
		},
		[router, current],
	);
}
